import { useState, useEffect } from 'react'
import { Clock } from 'lucide-react'
import { authApi } from '@/lib/api/auth'

export function SessionTimer() {
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null

    const updateTimer = async () => {
      try {
        const response = await authApi.verifyToken()
        if (response.session?.expires_at) {
          const expiresAt = response.session.expires_at * 1000
          const now = Date.now()
          const remaining = Math.floor((expiresAt - now) / 1000)
          
          if (remaining > 0) {
            setTimeRemaining(remaining)
            setIsLoading(false)
            
            // Start countdown
            if (interval) clearInterval(interval)
            interval = setInterval(() => {
              setTimeRemaining(prev => {
                if (prev === null || prev <= 0) {
                  if (interval) clearInterval(interval)
                  return 0
                }
                return prev - 1
              })
            }, 1000)
          }
        }
      } catch (error) {
        console.error('Failed to get session info:', error)
        setIsLoading(false)
      }
    }

    // Initial check
    updateTimer()

    // Refresh every 5 minutes to sync with server
    const refreshInterval = setInterval(updateTimer, 5 * 60 * 1000)

    return () => {
      if (interval) clearInterval(interval)
      clearInterval(refreshInterval)
    }
  }, [])

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (isLoading || timeRemaining === null) {
    return null
  }

  // Color coding based on time remaining
  const getColor = () => {
    if (timeRemaining <= 300) return 'text-red-500' // 5 minutes or less
    if (timeRemaining <= 600) return 'text-orange-500' // 10 minutes or less
    return 'text-gray-600 dark:text-gray-400'
  }

  return (
    <div className={`flex items-center gap-2 text-sm ${getColor()}`}>
      <Clock className="w-4 h-4" />
      <span className="font-mono">{formatTime(timeRemaining)}</span>
    </div>
  )
}